import React, { useCallback, useEffect, useMemo, useState } from "react";
import { adminApiRequest } from "../utils/adminApi";

export default function WeddingPackageBookings() {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const sortedBookings = useMemo(() => {
    return [...bookings].sort(
      (a, b) => new Date(b.created_at) - new Date(a.created_at),
    );
  }, [bookings]);

  const formatDate = (date) => {
    if (!date) return "-";
    const d = new Date(date);
    if (Number.isNaN(d.getTime())) return "-";
    return d.toLocaleDateString("en-GB");
  };

  const formatDateTime = (date) => {
    if (!date) return "-";
    const d = new Date(date);
    if (Number.isNaN(d.getTime())) return "-";
    return d.toLocaleString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getErrorMessage = (err, fallback) => {
    if (err.message === "REQUEST_TIMEOUT") {
      return "Request timed out. Please try again.";
    }
    if (err.message === "NETWORK_ERROR") {
      return "Network error. Please check your connection.";
    }
    if (err.message === "UNAUTHORIZED" || err.message === "SESSION_EXPIRED") {
      return "Your session has expired. Please sign in again.";
    }
    return err.message || fallback;
  };

  const loadBookings = useCallback(async () => {
    try {
      setLoading(true);
      setError("");

      const res = await adminApiRequest({
        script: "list_wedding_package_bookings",
      });

      if (!res?.success) {
        throw new Error(
          res?.message || "Failed to load wedding package bookings.",
        );
      }

      setBookings(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      setError(
        getErrorMessage(err, "Unable to load wedding package bookings."),
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadBookings();
  }, [loadBookings]);

  return (
    <section className="h-full min-h-0">
      <div className="flex h-full min-h-0 flex-col rounded-3xl border border-slate-200 bg-white shadow-sm">
        <div className="border-b border-slate-200 p-5">
          <h2 className="text-2xl font-semibold text-slate-900">
            Wedding Package Bookings
          </h2>
          <p className="mt-1 text-sm text-slate-500">
            View all completed wedding package bookings.
          </p>
        </div>

        {error && (
          <div className="px-5 pt-4">
            <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              {error}
            </div>
          </div>
        )}

        <div className="min-h-0 flex-1 overflow-auto p-5">
          {loading ? (
            <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-10 text-center text-sm text-slate-500">
              Loading wedding package bookings...
            </div>
          ) : sortedBookings.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 px-4 py-10 text-center">
              <p className="text-sm text-slate-500">
                No paid wedding package bookings yet.
              </p>
            </div>
          ) : (
            <div className="overflow-hidden rounded-2xl border border-slate-200">
              <div className="overflow-x-auto">
                <table className="min-w-full text-left">
                  <thead className="bg-slate-50">
                    <tr className="text-sm text-slate-600">
                      <th className="px-4 py-3 font-semibold">Ref</th>
                      <th className="px-4 py-3 font-semibold">Customer</th>
                      <th className="px-4 py-3 font-semibold">Package</th>
                      <th className="px-4 py-3 font-semibold">Event</th>
                      <th className="px-4 py-3 font-semibold">Notes</th>
                      <th className="px-4 py-3 font-semibold">Date & Time</th>
                      <th className="px-4 py-3 font-semibold">Location</th>
                      <th className="px-4 py-3 font-semibold">Amount</th>
                      <th className="px-4 py-3 font-semibold">Paid At</th>
                    </tr>
                  </thead>

                  <tbody className="divide-y divide-slate-200 bg-white">
                    {sortedBookings.map((item) => {
                      const amount = Number(
                        (item.amount_paid ?? item.package_price) || 0,
                      ).toFixed(2);

                      return (
                        <tr key={item.id} className="align-top">
                          <td className="px-4 py-4 text-sm font-medium text-slate-900">
                            {item.booking_ref || "-"}
                          </td>

                          <td className="px-4 py-4 text-sm text-slate-700">
                            <div className="font-medium text-slate-900">
                              {item.full_name}
                            </div>

                            <div className="text-xs text-slate-500">
                              {item.email}
                            </div>

                            {item.phone && (
                              <a
                                href={`tel:${item.phone}`}
                                className="text-xs text-indigo-600 hover:underline"
                              >
                                {item.phone}
                              </a>
                            )}
                          </td>

                          <td className="px-4 py-4 text-sm text-slate-700">
                            <div className="font-medium text-slate-900">
                              {item.package_name}
                            </div>

                            <div className="text-xs text-slate-500">
                              {item.package_coverage || "-"}
                            </div>

                            {item.features?.length > 0 && (
                              <div className="mt-2 space-y-1">
                                {item.features.slice(0, 2).map((feature, i) => (
                                  <div
                                    key={i}
                                    className="text-xs text-slate-600"
                                  >
                                    • {feature}
                                  </div>
                                ))}

                                {item.features.length > 2 && (
                                  <div className="text-xs text-indigo-600">
                                    +{item.features.length - 2} more
                                  </div>
                                )}
                              </div>
                            )}
                          </td>

                          <td className="px-4 py-4 text-sm text-slate-700">
                            {item.event_type || "Wedding Event"}
                          </td>

                          <td className="px-4 py-4 text-sm text-slate-700">
                            {item.coverage_notes ? (
                              <div className="text-xs text-indigo-600 whitespace-pre-wrap">
                                {item.coverage_notes}
                              </div>
                            ) : (
                              "-"
                            )}
                          </td>

                          <td className="px-4 py-4 text-sm text-slate-700">
                            <div className="font-medium text-slate-900">
                              {formatDate(item.event_date)}
                            </div>
                            <div className="text-xs text-indigo-600">
                              {item.start_time}
                            </div>
                          </td>

                          <td className="px-4 py-4 text-sm text-slate-700">
                            <div className="max-w-xs whitespace-normal break-words">
                              {item.location}
                            </div>
                          </td>

                          <td className="px-4 py-4 text-sm font-medium text-slate-900">
                            £{amount}
                          </td>

                          <td className="px-4 py-4 text-sm font-medium text-green-600">
                            {formatDateTime(item.paid_at)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
